import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../apiClient';
import type { LessonAccessLevel } from './lesson';

export interface Plan {
  id: string;
  name: string;
  price: number;
  lessons: number;
  mode: 'subscription' | 'payment';
  allowedLessonAccessLevels: LessonAccessLevel[];
  order: number;
}

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  nickName?: string;
  stripes: number;
  image: string;
  belt: string;
  startDate: string;
  instructor: boolean;
  plan: Plan | null;
  paidAt?: string;
  planBookings: number;
  totalBookings?: number;
  latestClass?: {
    id: number;
    startDate: string;
    type: string;
  };
  graduationHistory?: Graduation[];
}

export interface Graduation {
  id: number;
  userId: number;
  instructorId: number;
  graduatedAt: string;
  previousBelt: string;
  previousStripes: number;
  newBelt: string;
  newStripes: number;
  classCountAtGraduation: number;
  note?: string;
}

export const getUser = async (id: number) => {
  const { data } = await apiClient.get<User>(`/users/${id}`);
  return data;
};

export function useGetUser(
  id?: number,
  options?: { refetchInterval?: number; refetchOnWindowFocus?: boolean },
) {
  return useQuery(
    ['user', id],
    async () => getUser(id!),
    {
      enabled: id !== undefined,
      ...options,
    },
  );
}

interface GetUsersFilter {
  hasPlan?: boolean;
  instructor?: boolean;
}
export function useGetUsers(filter?: GetUsersFilter) {
  return useQuery(['users', filter], async () => {
    const params = new URLSearchParams();
    if (filter?.hasPlan) {
      params.set('hasPlan', 'true');
    }
    if (filter?.instructor !== undefined) {
      params.set('instructor', filter.instructor.toString());
    }
    const { data } = await apiClient.get<User[]>(`/users`, { params });
    return data;
  });
}

interface UserChangeset {
  planId?: string | null;
  paidAt?: string | null;
  belt?: string;
  stripes?: number;
  nickName?: string | null;
  instructor?: boolean;
}
export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, changeset }: { id: number; changeset: UserChangeset }) => {
      const { data } = await apiClient.patch<User>(`/users/${id}`, changeset);
      return data;
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['users']);
        queryClient.invalidateQueries(['user', variables.id]);
      },
    },
  );
}

export function useUpdateUserImage() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, image }: { id: number; image: File }) => {
      const formData = new FormData();
      formData.append('image', image);
      const { data } = await apiClient.patch<User>(`/users/${id}/image`, formData);
      return data;
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['users']);
        queryClient.invalidateQueries(['user', variables.id]);
      },
    },
  );
}

export function useGraduateUser() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      id,
      graduation,
    }: {
      id: number;
      graduation: { belt: string; stripes: number; note?: string | null };
    }) => {
      const { data } = await apiClient.post<User>(`/users/${id}/graduations`, graduation);
      return data;
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['users']);
        queryClient.invalidateQueries(['user', variables.id]);
      },
    },
  );
}
